import { Link } from "react-router-dom";
import {
  Award, BadgeCheck, Wrench, Clock, PackageCheck, MapPin, Phone,
  ArrowRight, ChevronDown,
} from "lucide-react";
import { useState } from "react";
import SEO from "../components/SEO";
import GoogleReviewsBadge from "../components/GoogleReviewsBadge";
import { ASSETS, BUSINESS, USP, FAQS } from "../lib/data";

const ICONS = { Award, BadgeCheck, Wrench, Clock, PackageCheck, MapPin };

export default function Home() {
  return (
    <>
      <SEO
        title="Il Velocipede | Rivenditore Ufficiale Piaggio e Kymco a Roma dal 1973"
        description="Storico rivenditore ufficiale Piaggio e Kymco a Roma Nuovo Salario. Vendita scooter, officina specializzata per moto, scooter e biciclette dal 1973."
        canonical="https://www.ilvelocipede.it/"
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={ASSETS.storefront} alt="Il Velocipede negozio Roma" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 lg:py-44 fade-up">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-white/90 text-xs uppercase tracking-[0.22em] font-semibold mb-5">
              <span className="w-8 h-px bg-[#fd0100]" />
              Roma Nord · Nuovo Salario · dal 1973
            </span>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Rivenditore Ufficiale <span className="text-[#fd0100]">Piaggio</span> e <span className="text-[#fd0100]">Kymco</span> a Roma dal 1973
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-200 max-w-2xl leading-relaxed">
              Vendita scooter, assistenza tecnica, officina specializzata per moto, scooter e biciclette nel cuore del quartiere Nuovo Salario.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/scooter" data-testid="hero-cta-scooter" className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm shadow-lg shadow-red-900/30">
                Scopri i Veicoli <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/officina-moto" data-testid="hero-cta-officina" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm bg-white text-[#013368] hover:bg-slate-100 transition">
                Prenota un Intervento
              </Link>
              <a href={`https://wa.me/${BUSINESS.whatsappRaw}`} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="btn-wa inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm">
                Contattaci su WhatsApp
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-md p-2 w-14 h-14 flex items-center justify-center">
                  <img src={ASSETS.piaggio} alt="Rivenditore Ufficiale Piaggio" className="w-10 h-10 object-contain" />
                </div>
                <div className="text-white text-sm">
                  <div className="font-semibold">Rivenditore Ufficiale</div>
                  <div className="text-slate-300">Piaggio</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-md p-2 w-14 h-14 flex items-center justify-center">
                  <img src={ASSETS.kymco} alt="Rivenditore Ufficiale Kymco" className="w-10 h-10 object-contain" />
                </div>
                <div className="text-white text-sm">
                  <div className="font-semibold">Rivenditore Ufficiale</div>
                  <div className="text-slate-300">Kymco</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHI SIAMO TEASER */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="overline">La nostra storia</div>
            <div className="divider-red mt-3 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Oltre <span className="text-[#013368]">50 anni</span> al servizio delle due ruote a Roma
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              Il Velocipede nasce nel <strong>1973</strong> nel quartiere Nuovo Salario e da allora rappresenta un punto di riferimento per chi cerca competenza, qualità e affidabilità nel mondo di scooter, moto e biciclette.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              In oltre cinque decenni abbiamo accompagnato generazioni di romani — dai primi Ciao alle moderne Vespa, dalle biciclette muscolari alle e-bike di ultima generazione. Un legame con il territorio fatto di fiducia, rapporto personale e lavoro fatto bene.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/chi-siamo" data-testid="about-cta" className="btn-secondary inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                Scopri la storia <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                <img src={ASSETS.workshopScooter} alt="Officina scooter Roma" className="w-full h-full object-cover" />
              </div>
              <div className="grid gap-4">
                <div className="bg-[#013368] text-white p-6 rounded-2xl">
                  <div className="font-display font-extrabold text-5xl">52+</div>
                  <div className="mt-2 text-slate-200 text-sm">anni di attività<br/>nel quartiere</div>
                </div>
                <div className="bg-[#fd0100] text-white p-6 rounded-2xl">
                  <div className="font-display font-extrabold text-3xl">1973</div>
                  <div className="mt-2 text-red-50 text-sm">l'anno in cui<br/>tutto è iniziato</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USP GRID */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="usp-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="overline">Perché sceglierci</div>
            <div className="divider-red mx-auto mt-3 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              I nostri punti di forza
            </h2>
            <p className="mt-4 text-slate-600">
              Esperienza, ufficialità dei brand e assistenza qualificata: tutto ciò che ti aspetti da uno storico rivenditore ufficiale di Roma Nord.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {USP.map((u, i) => {
              const Icon = ICONS[u.icon];
              return (
                <div key={i} data-testid={`usp-card-${i}`} className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#013368] hover:shadow-md transition-all">
                  <div className="w-12 h-12 rounded-xl bg-[#e6eff8] flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#013368]" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900">{u.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{u.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="overline">I nostri servizi</div>
            <div className="divider-red mt-3 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Tutto ciò che serve alle tue due ruote
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ServiceCard
              img={ASSETS.urbanRome}
              tag="Rivenditore Ufficiale"
              title="Scooter Piaggio & Kymco"
              desc="Tutta la gamma ufficiale con preventivi personalizzati, permute e finanziamenti."
              to="/scooter"
              testid="service-scooter"
            />
            <ServiceCard
              img={ASSETS.workshopScooter}
              tag="Officina"
              title="Moto e Scooter"
              desc="Tagliandi, diagnosi elettronica, riparazioni e revisione ministeriale."
              to="/officina-moto"
              testid="service-moto"
            />
            <ServiceCard
              img={ASSETS.workshopBike}
              tag="Officina"
              title="Biciclette & E-Bike"
              desc="Riparazioni, manutenzione e assistenza completa per ogni tipo di bici."
              to="/officina-bici"
              testid="service-bici"
            />
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS BADGE */}
      <GoogleReviewsBadge />

      {/* MAPPA + CONTATTI MINI */}
      <section className="py-20 md:py-28 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-stretch">
          <div>
            <div className="overline">Dove siamo</div>
            <div className="divider-red mt-3 mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Vieni a trovarci nel quartiere Nuovo Salario
            </h2>
            <div className="mt-8 space-y-5">
              <InfoRow icon={MapPin} label="Indirizzo" value={`${BUSINESS.address}, ${BUSINESS.city}`} />
              <InfoRow icon={Phone} label="Telefono" value={BUSINESS.phoneDisplay} href={`tel:${BUSINESS.phoneRaw}`} />
              <InfoRow icon={Clock} label="Orari" value="Lun-Ven 9-13 · 15:30-19:30  ·  Sab 9-13" />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="map-call-button" className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                <Phone className="w-4 h-4" /> Chiama Ora
              </a>
              <a href={BUSINESS.mapsLink} target="_blank" rel="noopener noreferrer" data-testid="map-directions" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                Indicazioni stradali
              </a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 min-h-[380px]">
            <iframe
              title="Il Velocipede - Mappa"
              src={BUSINESS.mapsEmbed}
              width="100%"
              height="100%"
              style={{ minHeight: "380px", border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />
    </>
  );
}

function ServiceCard({ img, tag, title, desc, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group block rounded-2xl overflow-hidden border border-slate-200 hover:border-[#013368] hover:shadow-lg transition-all bg-white">
      <div className="aspect-[16/10] overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className="p-6">
        <div className="text-xs uppercase tracking-widest font-semibold text-[#fd0100]">{tag}</div>
        <h3 className="font-display font-bold text-xl text-slate-900 mt-2">{title}</h3>
        <p className="mt-2 text-slate-600 text-sm leading-relaxed">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#013368] group-hover:gap-3 transition-all">
          Scopri di più <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const body = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-lg bg-[#e6eff8] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#013368]" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-slate-900 font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80 transition">{body}</a> : body;
}

function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="overline">FAQ</div>
          <div className="divider-red mx-auto mt-3 mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Domande frequenti
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <span className="font-semibold text-slate-900">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#013368] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-6 pb-6 text-slate-600 text-sm leading-relaxed" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
